/**
 * Scrolling Cards Component
 *
 * Implements a horizontal scrolling carousel of proposal cards with two modes:
 * 1. Default scrolling mode - horizontal scroll with progress bar
 * 2. Selected card mode - drawer opens, active card scales, prev/next nav
 *
 * @package oska
 */

/**
 * ScrollingCards class
 * @param {HTMLElement} element - The scrolling cards container with [data-scrolling-cards]
 */
class ScrollingCards {
  constructor(element) {
    this.container = element;
    this.track = element.querySelector(".js-scrolling-track");
    this.cardsWrapper =
      element.querySelector(".js-scrolling-cards-set:not([hidden])") ||
      element.querySelector(".js-scrolling-cards-set");
    this.cards = this.cardsWrapper
      ? this.cardsWrapper.querySelectorAll(".js-scrolling-card")
      : element.querySelectorAll(".js-scrolling-card");
    this.overlay = element.querySelector(".js-scrolling-overlay");
    this.progress = element.querySelector("[data-cards-progress]");
    this.drawer = element.querySelector(".js-scrolling-drawer");
    this.drawerClose = element.querySelector(".js-scrolling-drawer-close");
    this.drawerHeader = element.querySelector(".js-drawer-header");
    this.drawerTitle = element.querySelector(".js-drawer-title");
    this.drawerCount = element.querySelector(".js-drawer-count");
    this.drawerLoading = element.querySelector(".js-drawer-loading");
    this.drawerProposals = element.querySelector(".js-drawer-proposals");

    // Proposal modal elements
    this.proposalModal = element.querySelector(".js-proposal-modal");
    this.proposalModalOverlay = element.querySelector(
      ".js-proposal-modal-overlay",
    );
    this.proposalModalClose = element.querySelector(".js-proposal-modal-close");
    this.proposalModalLoading = element.querySelector(
      ".js-proposal-modal-loading",
    );
    this.proposalModalContent = element.querySelector(
      ".js-proposal-modal-content",
    );

    this.nav = element.querySelector(".js-scrolling-nav");
    this.prevBtn = element.querySelector("[data-scrolling-prev]");
    this.nextBtn = element.querySelector("[data-scrolling-next]");

    // Header overlay (covers header/filters when card is selected)
    this.headerOverlay = document.querySelector(".js-header-overlay");
    // Header logo (needs higher z-index when card is selected)
    this.headerLogo = document.querySelector(".js-header-logo");

    // Dashboard elements that need z-index lowered when card selected
    this.viewFilter = document.querySelector(".dashboard__view-filter");
    this.dashboardBottom = document.querySelector(".js-dashboard-bottom");

    this.activeIndex = -1;
    this.highlightedIndex = -1;
    this.isProgrammaticScroll = false;
    this.mode = "scroll"; // 'scroll' or 'selected'
    this.isScrolling = false;
    this.scrollTimeout = null;

    // Card dimensions
    this.cardWidth = 260;
    this.cardGap = 4;
    this.drawerWidth = 820;

    // Drag scrolling state
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragScrollLeft = 0;
    this.dragVelocity = 0;
    this.lastDragX = 0;
    this.lastDragTime = 0;

    // Spring physics state
    this.springAnimationId = null;
    this.currentVelocity = 0;
    this.targetScrollLeft = 0;
    this.isAnimating = false;

    // Spring physics constants (similar to Framer Motion defaults)
    this.springConfig = {
      stiffness: 300,
      damping: 30,
      mass: 0.5,
    };

    // Bounce effect config
    this.bounceConfig = {
      enabled: true,
      elasticity: 0.2, // How far past boundaries we can drag (20%)
      bounceStiffness: 300,
      bounceDamping: 30,
    };

    // AJAX loading state
    this.currentLoadController = null;
    this.proposalLoadController = null;

    // Current open proposal
    this.currentProposalId = null;
    this.currentProposalSlug = null;

    // Store dashboard URL for navigation
    this.dashboardUrl = window.APP_DATA?.dashboard_url || "/";

    // Filter elements and state
    this.partyFilter = element.querySelector(".js-drawer-filter-party");
    this.statusFilter = element.querySelector(".js-drawer-filter-status");
    this.partyFilterOptions =
      this.partyFilter?.querySelector(".js-select-options");
    this.statusFilterOptions =
      this.statusFilter?.querySelector(".js-select-options");
    this.currentFilters = { statuses: [], parties: [] };
    this.availableFilters = { statuses: [], parties: [] };

    // CSV modal elements
    this.csvBtn = element.querySelector("#drawer-csv-btn");
    this.csvModal = element.querySelector(".js-csv-modal");
    this.csvModalClose = element.querySelector(".js-csv-modal-close");
    this.csvDownloadFiltered = element.querySelector(
      ".js-csv-download-filtered",
    );

    // Bound focus trap handlers (for add/removeEventListener)
    this._drawerFocusTrap = this._createFocusTrap(this.drawer);
    this._proposalFocusTrap = this._createFocusTrap(this.proposalModal);

    // Drawer header reveal-on-scroll-up state
    this._drawerHeaderState = {
      lastTop: 0,
      downDelta: 0,
      upDelta: 0,
      hidden: false,
    };
    this._showDrawerHeaderUpThreshold = 30;
    this._hideDrawerHeaderDownThreshold = 50;

    this.init();
  }

  init() {
    this.bindEvents();
    this.updateProgress();
    this.updateDimensions();

    // Update dimensions on resize
    window.addEventListener("resize", () => {
      this.updateDimensions();
      if (this.mode === "selected") {
        this.centerActiveCard();
      }
    });

    // Handle browser back/forward navigation
    window.addEventListener("popstate", (e) => {
      if (e.state?.proposal) {
        // Navigate to a proposal
        this.openProposal(e.state.proposalId, false, false);
      } else if (e.state?.scrollingCards) {
        // Close proposal modal if open
        if (this.currentProposalId !== null) {
          this.closeProposal(false);
        }
        // Navigate to a card with URL filters
        this.selectCard(e.state.cardIndex, false, true);
      } else {
        // Close proposal modal if open
        if (this.currentProposalId !== null) {
          this.closeProposal(false);
        }
        // Back to dashboard
        if (this.mode === "selected") {
          this.exitSelectedMode(false);
        }
      }
    });

    // Check URL on load for direct taxonomy/proposal access
    this.checkUrlAndSelectCard();
  }

  /**
   * Parse URL and select matching card if on a taxonomy or proposal page
   */
  checkUrlAndSelectCard() {
    const path = window.location.pathname;

    // Match /proposal/{slug}/
    const proposalMatch = path.match(/^\/proposal\/([^/]+)\/?$/);
    if (proposalMatch) {
      this.handleProposalUrl(proposalMatch[1]);
      return;
    }

    // Match /sector/{slug}/ or /responsible-party/{slug}/
    const sectorMatch = path.match(/^\/sector\/([^/]+)\/?$/);
    const partyMatch = path.match(/^\/responsible-party\/([^/]+)\/?$/);

    let taxonomy = null;
    let slug = null;

    if (sectorMatch) {
      taxonomy = "sector";
      slug = sectorMatch[1];
    } else if (partyMatch) {
      taxonomy = "responsible_party";
      slug = partyMatch[1];
    }

    if (!taxonomy || !slug) return;

    // The card UI has two mutually exclusive data sets. Deep-link restoration
    // has to activate the matching set before it can look up a card by slug.
    const viewIndex = taxonomy === "sector" ? 0 : 1;

    // Switch to the correct card set if needed
    const correctCardSet = this.container.querySelector(
      `[data-card-set="${taxonomy}"]`,
    );
    const currentlyVisible = this.container.querySelector(
      ".js-scrolling-cards-set:not([hidden])",
    );

    if (correctCardSet && currentlyVisible !== correctCardSet) {
      // Update view filter UI state
      document.dispatchEvent(
        new CustomEvent("view-filter:set", {
          detail: { index: viewIndex },
        }),
      );

      // Dispatch view-filter:change to switch card sets
      document.dispatchEvent(
        new CustomEvent("view-filter:change", {
          detail: { index: viewIndex },
        }),
      );

      // The view-filter change hides one card set and reveals the other on the
      // next frame, so we defer slug lookup until the DOM has switched.
      requestAnimationFrame(() => {
        this.selectCardBySlug(slug, taxonomy);
      });
    } else {
      // Already on correct card set, select immediately
      this.selectCardBySlug(slug, taxonomy);
    }
  }

  /**
   * Handle direct access to a proposal URL
   * Fetches proposal data, selects related sector, opens proposal modal
   * @param {string} proposalSlug - Proposal post slug
   */
  async handleProposalUrl(proposalSlug) {
    // Proposal URLs are resolved in two hops: slug -> proposal/sector metadata,
    // then sector card selection, then modal open by numeric proposal ID.
    try {
      const restUrl = (window.APP_DATA?.rest_url || "/wp-json/") + "oska/v1";
      const response = await fetch(
        `${restUrl}/proposals/by-slug/${encodeURIComponent(proposalSlug)}`,
        {
          method: "GET",
        },
      );

      const result = await response.json();

      if (response.ok && result) {
        const { proposal_id, sector } = result;

        // If we have a sector, switch to it and select the card
        if (sector) {
          // Ensure sector view is active
          const viewIndex = 0; // sector is always index 0
          const correctCardSet = this.container.querySelector(
            '[data-card-set="sector"]',
          );
          const currentlyVisible = this.container.querySelector(
            ".js-scrolling-cards-set:not([hidden])",
          );

          if (correctCardSet && currentlyVisible !== correctCardSet) {
            // Update view filter UI state
            document.dispatchEvent(
              new CustomEvent("view-filter:set", {
                detail: { index: viewIndex },
              }),
            );

            // Dispatch view-filter:change to switch card sets
            document.dispatchEvent(
              new CustomEvent("view-filter:change", {
                detail: { index: viewIndex },
              }),
            );
          }

          // Wait a frame for card set switch so card references point at the
          // now-visible sector set before we attempt to select a card.
          await new Promise((resolve) => requestAnimationFrame(resolve));

          // Refresh cards reference after potential switch
          this.cardsWrapper = this.container.querySelector(
            ".js-scrolling-cards-set:not([hidden])",
          );
          this.cards =
            this.cardsWrapper?.querySelectorAll(".js-scrolling-card") || [];

          // Select the parent sector instantly so the proposal modal opens in
          // the same UI state a user would reach through normal navigation.
          const cardIndex = this.findCardBySlug(sector.slug, "sector");
          if (cardIndex >= 0) {
            // Disable transitions temporarily
            this.container.classList.add("no-transition");
            this.selectCard(cardIndex, false);

            // Re-enable transitions
            requestAnimationFrame(() => {
              this.container.classList.remove("no-transition");
            });
          }
        }

        // Open the proposal modal (instant, no animation)
        this.openProposal(proposal_id, false, false);

        // Replace history state
        history.replaceState(
          {
            scrollingCards: true,
            proposal: true,
            proposalId: proposal_id,
            slug: proposalSlug,
            cardIndex: this.activeIndex,
          },
          "",
          window.location.pathname,
        );
      }
    } catch (error) {
      console.error("Error loading proposal from URL:", error);
    }
  }

  /**
   * Select card by slug and taxonomy
   * @param {string} slug - Term slug
   * @param {string} taxonomy - Taxonomy name
   * @param {boolean} applyUrlFilters - Whether to apply filters from URL (default: true)
   */
  selectCardBySlug(slug, taxonomy, applyUrlFilters = true) {
    const cardIndex = this.findCardBySlug(slug, taxonomy);

    if (cardIndex >= 0) {
      // Replace current history state so back goes to dashboard (preserve query string)
      history.replaceState(
        { scrollingCards: true, cardIndex, taxonomy, slug },
        "",
        window.location.pathname + window.location.search,
      );

      // Select the card without pushing another URL, apply URL filters
      this.selectCard(cardIndex, false, applyUrlFilters);
    }
  }

  /**
   * Find card index by slug and taxonomy
   * @param {string} slug - Term slug to find
   * @param {string} taxonomy - Taxonomy to match
   * @returns {number} Card index or -1 if not found
   */
  findCardBySlug(slug, taxonomy) {
    for (let i = 0; i < this.cards.length; i++) {
      const card = this.cards[i];
      if (card.dataset.slug === slug && card.dataset.taxonomy === taxonomy) {
        return i;
      }
    }
    return -1;
  }

  updateDimensions() {
    // Get actual card dimensions if available
    if (this.cards[0]) {
      const cardEl = this.cards[0].querySelector(".proposal-card");
      if (cardEl) {
        this.cardWidth = cardEl.offsetWidth || 260;
      }
    }

    // Get drawer width from CSS variable
    const rootStyle = getComputedStyle(document.documentElement);
    const drawerWidthVar = rootStyle.getPropertyValue("--drawer-width").trim();
    if (drawerWidthVar) {
      this.drawerWidth = parseInt(drawerWidthVar, 10) || 820;
    }

    // Fallback: check responsive width
    if (window.innerWidth <= 1279 && window.innerWidth > 1024) {
      this.drawerWidth = 600;
    } else if (window.innerWidth <= 1024) {
      this.drawerWidth = window.innerWidth;
    }

    this.updateProgressVisibility();
  }

  updateProgressVisibility() {
    if (!this.track || !this.progress) return;

    const maxScroll = Math.max(
      0,
      this.track.scrollWidth - this.track.clientWidth,
    );
    const isScrollable = maxScroll > 1;
    const indicator = this.progress.querySelector(".js-progress-indicator");

    this.progress.classList.toggle("opacity-0", !isScrollable);
    this.progress.classList.toggle("pointer-events-none", !isScrollable);
    this.progress.setAttribute("aria-hidden", isScrollable ? "false" : "true");

    if (indicator) {
      indicator.setAttribute("tabindex", isScrollable ? "0" : "-1");
    }
  }

  bindEvents() {
    // Card click - use event delegation so it works after card set changes
    if (this.track) {
      this.track.addEventListener("click", (e) => {
        const card = e.target.closest(".js-scrolling-card");
        if (!card) return;

        // Toggle icon: in selected mode, exit selected mode; otherwise ignore
        if (e.target.closest("[data-toggle]")) {
          if (this.mode === "selected") {
            e.preventDefault();
            this.exitSelectedMode();
          }
          return;
        }

        // Prevent link navigation (progressive enhancement)
        e.preventDefault();

        // Find the index of this card in the current cards array
        const index = Array.from(this.cards).indexOf(card);
        if (index >= 0) {
          this.selectCard(index);
        }
      });

      // Keyboard support using event delegation
      this.track.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          const card = e.target.closest(".js-scrolling-card");
          if (!card) return;
          e.preventDefault();
          const index = Array.from(this.cards).indexOf(card);
          if (index >= 0) {
            this.selectCard(index);
          }
        }
      });
    }

    // Track scroll for progress bar and highlight removal
    if (this.track) {
      this.track.addEventListener("scroll", () => {
        this.handleScroll();
      });
    }

    // Drawer close
    if (this.drawerClose) {
      this.drawerClose.addEventListener("click", () => {
        this.exitSelectedMode();
      });
    }

    // Drawer scroll: reveal header on scroll up, hide on scroll down
    if (this.drawer && this.drawerHeader) {
      this.drawer.addEventListener(
        "scroll",
        () => {
          this.handleDrawerScroll();
        },
        { passive: true },
      );
    }

    // Proposal modal close
    if (this.proposalModalClose) {
      this.proposalModalClose.addEventListener("click", () => {
        this.closeProposal();
      });
    }

    // Proposal modal overlay click - close modal
    if (this.proposalModalOverlay) {
      this.proposalModalOverlay.addEventListener("click", () => {
        this.closeProposal();
      });
    }

    // Overlay click - clear highlight
    if (this.overlay) {
      this.overlay.addEventListener("click", () => {
        if (this.highlightedIndex !== -1) {
          this.clearHighlight();
        }
      });
    }

    // Prev/Next navigation
    if (this.prevBtn) {
      this.prevBtn.addEventListener("click", () => {
        this.navigate(-1);
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener("click", () => {
        this.navigate(1);
      });
    }

    // Listen for highlight events from dropdown/external
    document.addEventListener("scrolling-cards:highlight", (e) => {
      if (e.detail.cardIndex !== undefined) {
        this.highlightCard(e.detail.cardIndex);
      }
    });

    // Listen for clear highlight events
    document.addEventListener("scrolling-cards:clear-highlight", () => {
      this.clearHighlight();
    });

    // ESC to exit selected mode or close proposal modal
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        // If a custom-select dropdown is open, let it handle Escape first
        if (document.querySelector(".js-custom-select.is-open")) return;

        // Close proposal modal first if open
        if (this.currentProposalId !== null) {
          this.closeProposal();
        } else if (this.mode === "selected") {
          this.exitSelectedMode();
        }
      }
    });

    // Drag scrolling
    this.bindDragEvents();

    // Wheel to horizontal scroll
    this.bindWheelEvents();

    // Listen for filter changes in drawer
    this.bindFilterEvents();

    // Listen for progress bar changes (drag/click)
    this.bindProgressEvents();

    // CSV modal events
    this.bindCsvModalEvents();
  }

  /**
   * Bind progress bar interaction events
   */
  bindProgressEvents() {
    if (!this.progress) return;

    // Discrete card navigation (click, keyboard)
    this.progress.addEventListener("cards-progress:change", (e) => {
      const { current, total } = e.detail;
      if (current >= 1 && current <= total) {
        this.scrollToCard(current - 1, true);
      }
    });

    // Continuous scroll during drag or animated click
    this.progress.addEventListener("cards-progress:scroll", (e) => {
      const { progress, animated } = e.detail;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const targetScroll = progress * maxScroll;

      if (animated) {
        this.isProgrammaticScroll = true;
        this.track.scrollTo({ left: targetScroll, behavior: "smooth" });
        // Reset flag after smooth scroll finishes
        clearTimeout(this._smoothScrollTimer);
        this._smoothScrollTimer = setTimeout(() => {
          this.isProgrammaticScroll = false;
        }, 350);
      } else {
        this.track.scrollLeft = targetScroll;
      }
    });
  }

  /**
   * Bind filter change events
   */
  bindFilterEvents() {
    if (!this.drawer) return;

    // Listen for custom-select:change events from filter dropdowns
    this.drawer.addEventListener("custom-select:change", (e) => {
      const filterType = e.target.closest("[data-filter]")?.dataset.filter;
      if (!filterType) return;

      // Update current filters
      const values = e.detail?.values || [];
      if (filterType === "party") {
        this.currentFilters.parties = values;
      } else if (filterType === "status") {
        this.currentFilters.statuses = values;
      }

      // Update URL with filter state
      this.updateFilterUrl();

      // Reload drawer content with new filters
      if (this.activeIndex >= 0) {
        const card = this.cards[this.activeIndex];
        const termId = card.dataset.termId;
        const taxonomy = card.dataset.taxonomy;
        this.loadDrawerContent(termId, taxonomy, this.currentFilters, false);
      }
    });
  }

  /**
   * Get current filter values from URL query params
   * @returns {{ statuses: string[], parties: string[] }}
   */
  getFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const statuses = params.get("status")?.split(",").filter(Boolean) || [];
    const parties = params.get("party")?.split(",").filter(Boolean) || [];
    return { statuses, parties };
  }

  /**
   * Update URL query params with current filter state
   */
  updateFilterUrl() {
    const url = new URL(window.location.href);

    // Update or remove status param
    if (this.currentFilters.statuses.length > 0) {
      url.searchParams.set("status", this.currentFilters.statuses.join(","));
    } else {
      url.searchParams.delete("status");
    }

    // Update or remove party param
    if (this.currentFilters.parties.length > 0) {
      url.searchParams.set("party", this.currentFilters.parties.join(","));
    } else {
      url.searchParams.delete("party");
    }

    // Update URL without triggering navigation
    history.replaceState(history.state, "", url.toString());
  }

  /**
   * Clear filter URL params
   */
  clearFilterUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete("status");
    url.searchParams.delete("party");
    history.replaceState(history.state, "", url.toString());
  }

  /**
   * Update filter dropdown options based on available options from AJAX response
   * @param {Array} statuses - Available status options [{term_id, slug, name, color}]
   * @param {Array} parties - Available party options [{term_id, slug, name}]
   */
  updateFilterOptions(statuses = [], parties = []) {
    this.availableFilters = { statuses, parties };

    // Update status filter options
    if (this.statusFilterOptions) {
      this.statusFilterOptions.innerHTML = statuses
        .map(
          (s) => `
        <button type="button" class="js-select-option flex items-center justify-between gap-8 p-12 pl-12 mb-4 w-full bg-menu-bg border-none rounded-8 font-nunito font-bold text-14 leading-tight text-text-dark text-left cursor-pointer transition-colors duration-150 hover:bg-blue-100 hover:text-brand-blue last:mb-0" role="option" aria-selected="false" data-value="${s.slug}">
          <span>${s.name}</span>
          <span class="shrink-0 flex items-center justify-center w-16 h-16 bg-white rounded-4 transition-colors duration-150">
            <svg class="w-16 h-16 text-brand-blue opacity-0 transition-opacity duration-150" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4.8 8.764L6.427 10.4L11.2 5.6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </button>
      `,
        )
        .join("");

      // Re-initialize the CustomSelect instance
      this.reinitFilterSelect(this.statusFilter, this.currentFilters.statuses);
    }

    // Update party filter options
    if (this.partyFilterOptions) {
      const grouped = this.buildPartyGroups(parties);

      // Toggles render next to every category heading.
      const toggleableIds = new Set(
        grouped.map((g) => g.id || g.slug || g.name),
      );

      // Render the scrollable list with a heading (and inline toggle) per group.
      this.partyFilterOptions.innerHTML = this.renderPartyGroupList(
        grouped,
        toggleableIds,
      );

      // Re-initialize the CustomSelect instance
      this.reinitFilterSelect(this.partyFilter, this.currentFilters.parties);

      // Bind inline "Vali kõik" / "Tühista valikud" toggle behaviour
      this.bindPartyGroupToggles();
    }
  }

  /**
   * Group leaf party options by their immediate parent (preserves input order).
   *
   * @param {Array} parties - [{term_id, slug, name, parent_id, parent_name, parent_slug, parent_parent_id}]
   * @returns {Array<{id:number,name:string,slug:string,parentParentId:number,items:Array}>}
   */
  buildPartyGroups(parties) {
    const groups = new Map();
    (parties || []).forEach((p) => {
      const key = p.parent_id
        ? `id:${p.parent_id}`
        : `name:${p.parent_name || ""}`;
      if (!groups.has(key)) {
        groups.set(key, {
          id: p.parent_id || 0,
          name: p.parent_name || "Muud",
          slug: p.parent_slug || "",
          parentParentId: p.parent_parent_id || 0,
          items: [],
        });
      }
      groups.get(key).items.push(p);
    });
    return Array.from(groups.values());
  }

  /**
   * Render the scrollable list: a non-selectable category heading followed by
   * its leaf option buttons. For groups whose id is in `toggleableIds`, an
   * inline "Vali kõik" / "Tühista valikud" toggle is rendered next to the
   * heading.
   *
   * @param {Array} groups
   * @param {Set} [toggleableIds]
   * @returns {string}
   */
  renderPartyGroupList(groups, toggleableIds) {
    if (!groups || groups.length === 0) return "";

    const optionMarkup = (p) => `
      <button type="button" class="js-select-option flex items-center justify-between gap-8 p-12 pl-12 mb-4 w-full bg-menu-bg border-none rounded-8 font-nunito font-bold text-14 leading-tight text-text-dark text-left cursor-pointer transition-colors duration-150 hover:bg-blue-100 hover:text-brand-blue last:mb-0" role="option" aria-selected="false" data-value="${p.slug}">
        <span>${p.name}</span>
        <span class="shrink-0 flex items-center justify-center w-16 h-16 bg-white rounded-4 transition-colors duration-150">
          <svg class="w-16 h-16 text-brand-blue opacity-0 transition-opacity duration-150" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4.8 8.764L6.427 10.4L11.2 5.6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      </button>
    `;

    const toggleMarkup = (group) => {
      const slugs = group.items.map((i) => i.slug).join(",");
      return `
        <button type="button" class="js-party-group-toggle inline-flex items-center gap-8 font-nunito text-14 text-text-dark bg-transparent border-none p-0 cursor-pointer" data-group-slugs="${slugs}" aria-pressed="false">
          <span class="js-party-group-toggle-label whitespace-nowrap">Vali kõik</span>
        </button>
      `;
    };

    return groups
      .map((group) => {
        const groupId = group.id || group.slug || group.name;
        const showToggle = toggleableIds && toggleableIds.has(groupId);
        return `
          <div class="js-party-group mb-12 last:mb-0" data-group-id="${groupId}">
            <div class="flex items-center justify-between gap-12 py-12">
              <div class="font-nunito text-14 text-text-muted whitespace-nowrap overflow-hidden text-ellipsis">${group.name}</div>
              ${showToggle ? toggleMarkup(group) : ""}
            </div>
            ${group.items.map(optionMarkup).join("")}
          </div>
        `;
      })
      .join("");
  }

  /**
   * Bind click handlers to the group toggle buttons in the party summary.
   * Toggles all leaf options in the group on/off via the CustomSelect
   * instance. Visual state (checkbox + label) reflects whether all items in
   * the group are currently selected.
   */
  bindPartyGroupToggles() {
    if (!this.partyFilter) return;
    const instance = this.partyFilter._customSelect;
    if (!instance) return;

    const buttons = this.partyFilter.querySelectorAll(".js-party-group-toggle");

    // Use the instance dropdown (which may be portaled to <body>) so we can
    // always reach the option elements even when the select is inside a drawer.
    const optionRoot = instance.dropdown || this.partyFilter;

    const updateButton = (btn) => {
      const slugs = (btn.dataset.groupSlugs || "").split(",").filter(Boolean);
      if (!slugs.length) return;
      const anySelected = slugs.some((s) => instance.selectedValues.has(s));
      btn.setAttribute("aria-pressed", anySelected ? "true" : "false");
      const label = btn.querySelector(".js-party-group-toggle-label");
      const box = btn.querySelector(".js-party-group-toggle-box");
      const check = box?.querySelector("svg");
      if (label)
        label.textContent = anySelected ? "Tühista valikud" : "Vali kõik";
      if (box) {
        box.classList.toggle("bg-brand-blue", anySelected);
        box.classList.toggle("border-brand-blue", anySelected);
        box.classList.toggle("bg-white", !anySelected);
        box.classList.toggle("border-gray", !anySelected);
      }
      if (check) {
        check.classList.toggle("opacity-100", anySelected);
        check.classList.toggle("text-white", anySelected);
        check.classList.toggle("opacity-0", !anySelected);
        check.classList.toggle("text-brand-blue", !anySelected);
      }
    };

    buttons.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const slugs = (btn.dataset.groupSlugs || "").split(",").filter(Boolean);
        const anySelected = slugs.some((s) => instance.selectedValues.has(s));

        slugs.forEach((s) => {
          const opt = optionRoot.querySelector(
            `.js-select-option[data-value="${CSS.escape(s)}"]`,
          );
          if (!opt) return;
          const isSelected = instance.selectedValues.has(s);
          // If any item in the group is selected -> deselect all; otherwise select all.
          if (anySelected && isSelected) {
            instance.toggleOption(opt);
          } else if (!anySelected && !isSelected) {
            instance.toggleOption(opt);
          }
        });

        buttons.forEach(updateButton);
      });

      updateButton(btn);
    });

    // Keep button labels in sync with individual option clicks and
    // top-level "Vali kõik" / "Tühista valikud" toggle.
    // Store the latest references so the event handler always works with
    // current DOM nodes (the summary may be re-rendered or portaled).
    this.partyFilter._groupButtons = buttons;
    this.partyFilter._groupUpdateButton = updateButton;

    if (!this.partyFilter._groupChangeBound) {
      this.partyFilter.addEventListener("custom-select:change", () => {
        const btns = this.partyFilter._groupButtons;
        const updater = this.partyFilter._groupUpdateButton;
        if (btns && updater) btns.forEach(updater);
      });
      this.partyFilter._groupChangeBound = true;
    }
  }

  /**
   * Re-initialize a CustomSelect element after DOM updates
   * @param {HTMLElement} selectEl - The custom select root element
   * @param {string[]} selectedValues - Values to pre-select
   */
  reinitFilterSelect(selectEl, selectedValues = []) {
    if (!selectEl) return;

    // Refresh the CustomSelect instance so keyboard navigation works on new options
    const instance = selectEl._customSelect;
    if (instance) {
      instance.refreshOptions();
      instance.setSelectedValues(selectedValues);
      return;
    }

    const options = selectEl.querySelectorAll(".js-select-option");
    const badge = selectEl.querySelector(".js-select-badge");

    // Set up selection state
    options.forEach((option) => {
      const value = option.dataset.value;
      const isSelected = selectedValues.includes(value);

      if (isSelected) {
        option.classList.add("is-selected");
        option.setAttribute("aria-selected", "true");
      } else {
        option.classList.remove("is-selected");
        option.setAttribute("aria-selected", "false");
      }

      // Add click handler for toggling
      option.addEventListener("click", () => {
        const wasSelected = option.classList.contains("is-selected");

        if (wasSelected) {
          option.classList.remove("is-selected");
          option.setAttribute("aria-selected", "false");
        } else {
          option.classList.add("is-selected");
          option.setAttribute("aria-selected", "true");
        }

        // Update badge
        const selectedCount = selectEl.querySelectorAll(
          ".js-select-option.is-selected",
        ).length;
        if (badge) {
          badge.textContent = selectedCount > 0 ? selectedCount : "";
        }

        // Dispatch change event
        const selectedVals = Array.from(
          selectEl.querySelectorAll(".js-select-option.is-selected"),
        ).map((o) => o.dataset.value);

        selectEl.dispatchEvent(
          new CustomEvent("custom-select:change", {
            detail: { values: selectedVals },
            bubbles: true,
          }),
        );
      });
    });

    // Update badge with initial count
    const selectedCount = selectedValues.length;
    if (badge) {
      badge.textContent = selectedCount > 0 ? selectedCount : "";
    }
  }

  /**
   * Clear all filters and reset state
   */
  clearFilters() {
    this.currentFilters = { statuses: [], parties: [] };

    // Clear selected state in filter dropdowns (both DOM and CustomSelect instances)
    [this.statusFilter, this.partyFilter].forEach((filter) => {
      if (!filter) return;

      // If a CustomSelect instance exists, reset its internal state silently
      const instance = filter._customSelect;
      if (instance) {
        instance.selectedValues.clear();
        instance.options.forEach((option) => {
          option.classList.remove("is-selected");
          option.setAttribute("aria-selected", "false");
        });
        instance.updateBadge();
        instance.updateClearButton();
        return;
      }

      // Fallback: clear DOM directly
      const options = filter.querySelectorAll(".js-select-option");
      const badge = filter.querySelector(".js-select-badge");

      options.forEach((option) => {
        option.classList.remove("is-selected");
        option.setAttribute("aria-selected", "false");
      });

      if (badge) {
        badge.textContent = "";
      }
    });

    this.clearFilterUrl();
  }

  /**
   * Bind CSV modal events
   */
  bindCsvModalEvents() {
    if (!this.csvBtn || !this.csvModal) return;

    // Open modal on CSV button click
    this.csvBtn.addEventListener("click", () => {
      this.openCsvModal();
    });

    // Close modal on close button click
    if (this.csvModalClose) {
      this.csvModalClose.addEventListener("click", () => {
        this.closeCsvModal();
      });
    }

    // Close modal on overlay click
    this.csvModal.addEventListener("click", (e) => {
      if (e.target === this.csvModal) {
        this.closeCsvModal();
      }
    });

    // Close modal on ESC key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.csvModal.classList.contains("is-open")) {
        this.closeCsvModal();
      }
    });

    // Download filtered CSV
    if (this.csvDownloadFiltered) {
      this.csvDownloadFiltered.addEventListener("click", () => {
        this.downloadFilteredCsv();
      });
    }
  }

  /**
   * Open CSV download modal
   */
  openCsvModal() {
    if (!this.csvModal) return;
    this.csvModal.classList.add("is-open");
    document.body.style.overflow = "hidden";
    // Focus trap: focus the first button
    const firstBtn = this.csvModal.querySelector("button, a");
    if (firstBtn) firstBtn.focus();
  }

  /**
   * Close CSV download modal
   */
  closeCsvModal() {
    if (!this.csvModal) return;
    this.csvModal.classList.remove("is-open");
    // Only restore body scroll if drawer is not open
    if (this.activeIndex < 0) {
      document.body.style.overflow = "";
    }
    // Return focus to CSV button
    if (this.csvBtn) this.csvBtn.focus();
  }

  /**
   * Download filtered CSV via AJAX POST
   */
  downloadFilteredCsv() {
    if (this.activeIndex < 0) return;

    const card = this.cards[this.activeIndex];
    const termId = card.dataset.termId;
    const taxonomy = card.dataset.taxonomy;

    // Build REST URL with query params
    const restUrl = (window.APP_DATA?.rest_url || "/wp-json/") + "oska/v1";
    const params = new URLSearchParams();
    params.set("term_id", termId);
    params.set("taxonomy", taxonomy);

    // Add current filters
    this.currentFilters.statuses.forEach((status) => {
      params.append("statuses[]", status);
    });
    this.currentFilters.parties.forEach((party) => {
      params.append("parties[]", party);
    });

    // Open CSV download in new tab
    window.open(
      `${restUrl}/proposals/export-csv?${params.toString()}`,
      "_blank",
    );

    // Close modal after triggering download
    this.closeCsvModal();
  }

  /**
   * Bind drag scrolling events for mouse/touch
   */
  bindDragEvents() {
    if (!this.track) return;

    // Mouse events
    this.track.addEventListener("mousedown", (e) => this.handleDragStart(e));
    document.addEventListener("mousemove", (e) => this.handleDragMove(e));
    document.addEventListener("mouseup", (e) => this.handleDragEnd(e));

    // Touch events for mobile
    this.track.addEventListener("touchstart", (e) => this.handleDragStart(e), {
      passive: true,
    });
    this.track.addEventListener("touchmove", (e) => this.handleDragMove(e), {
      passive: false,
    });
    this.track.addEventListener("touchend", (e) => this.handleDragEnd(e));

    // Prevent default drag behavior on images and links
    this.track.addEventListener("dragstart", (e) => e.preventDefault());
  }

  /**
   * Get scroll boundaries
   */
  getScrollBounds() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    return { min: 0, max: Math.max(0, maxScroll) };
  }

  /**
   * Apply elastic/rubber band effect when dragging past boundaries
   */
  applyElasticScroll(targetScroll) {
    const bounds = this.getScrollBounds();
    const elasticity = this.bounceConfig.elasticity;

    if (targetScroll < bounds.min) {
      // Past left edge - apply resistance
      const overscroll = bounds.min - targetScroll;
      return bounds.min - overscroll * elasticity;
    } else if (targetScroll > bounds.max) {
      // Past right edge - apply resistance
      const overscroll = targetScroll - bounds.max;
      return bounds.max + overscroll * elasticity;
    }
    return targetScroll;
  }

  /**
   * Handle drag start
   */
  handleDragStart(e) {
    // Don't drag in selected mode
    if (this.mode === "selected") return;

    const clientX = e.type.startsWith("touch")
      ? e.touches[0].clientX
      : e.clientX;

    // Stop any ongoing animation
    this.stopSpringAnimation();

    this.isDragging = true;
    this.hasDragged = false; // Track if we've actually moved (for click detection)
    this.dragStartX = clientX;
    this.dragScrollLeft = this.track.scrollLeft;
    this.lastDragX = clientX;
    this.lastDragTime = performance.now();
    this.dragVelocity = 0;
    this.currentVelocity = 0;

    this.track.style.cursor = "grabbing";
    this.track.style.userSelect = "none";
  }

  /**
   * Handle drag move with elastic scrolling
   */
  handleDragMove(e) {
    if (!this.isDragging) return;

    const clientX = e.type.startsWith("touch")
      ? e.touches[0].clientX
      : e.clientX;
    const currentTime = performance.now();

    // Calculate distance moved from start
    const dx = clientX - this.dragStartX;

    // Only consider it a real drag after moving beyond threshold (5px)
    const dragThreshold = 5;
    if (!this.hasDragged && Math.abs(dx) > dragThreshold) {
      this.hasDragged = true;
      this.container.classList.add("is-dragging");
    }

    // Calculate velocity for momentum (pixels per millisecond)
    const timeDelta = currentTime - this.lastDragTime;
    if (timeDelta > 0) {
      const instantVelocity = (this.lastDragX - clientX) / timeDelta;
      // Smooth velocity calculation using exponential moving average
      this.dragVelocity = this.dragVelocity * 0.7 + instantVelocity * 0.3;
    }
    this.lastDragX = clientX;
    this.lastDragTime = currentTime;

    // Only scroll if we've exceeded drag threshold
    if (this.hasDragged) {
      let targetScroll = this.dragScrollLeft - dx;

      // Apply elastic effect at boundaries
      if (this.bounceConfig.enabled) {
        targetScroll = this.applyElasticScroll(targetScroll);
      } else {
        // Clamp to boundaries
        const bounds = this.getScrollBounds();
        targetScroll = Math.max(bounds.min, Math.min(bounds.max, targetScroll));
      }

      this.track.scrollLeft = targetScroll;
    }

    // Prevent text selection while dragging
    if (e.type === "touchmove" && Math.abs(dx) > dragThreshold) {
      e.preventDefault();
    }
  }

  /**
   * Handle drag end with spring physics momentum and bounce
   */
  handleDragEnd(e) {
    if (!this.isDragging) return;

    const wasDragging = this.hasDragged;

    this.isDragging = false;
    this.track.style.cursor = "";
    this.track.style.userSelect = "";
    this.container.classList.remove("is-dragging");

    // If we didn't actually drag (just clicked), don't apply momentum
    if (!wasDragging) {
      return;
    }

    const bounds = this.getScrollBounds();
    const currentScroll = this.track.scrollLeft;

    // Drag release has two exit paths: either snap back into bounds if the user
    // over-pulled the track, or continue with momentum while still in bounds.
    if (currentScroll < bounds.min || currentScroll > bounds.max) {
      // Bounce back to boundary with spring animation
      const targetScroll = currentScroll < bounds.min ? bounds.min : bounds.max;
      this.animateSpringTo(targetScroll, 0);
    } else if (Math.abs(this.dragVelocity) > 0.1) {
      // Apply momentum with spring deceleration
      // Convert velocity from px/ms to initial velocity for spring
      const initialVelocity = this.dragVelocity * 1000; // px/s
      this.animateMomentum(initialVelocity);
    }
  }

  /**
   * Animate scroll position using spring physics
   */
  animateSpringTo(targetScroll, initialVelocity = 0) {
    this.stopSpringAnimation();

    const { stiffness, damping, mass } = this.springConfig;
    let position = this.track.scrollLeft;
    let velocity = initialVelocity;
    const target = targetScroll;

    const animate = () => {
      // Spring force: F = -k * x (where x is displacement from target)
      const displacement = position - target;
      const springForce = -stiffness * displacement;

      // Damping force: F = -c * v
      const dampingForce = -damping * velocity;

      // Acceleration: F = ma, so a = F/m
      const acceleration = (springForce + dampingForce) / mass;

      // Update velocity and position (using small time step)
      const dt = 1 / 60; // ~60fps
      velocity += acceleration * dt;
      position += velocity * dt;

      // Apply position
      this.track.scrollLeft = position;

      // Check if animation should stop
      const positionDelta = Math.abs(position - target);
      const velocityMagnitude = Math.abs(velocity);

      if (positionDelta < 0.5 && velocityMagnitude < 10) {
        // Close enough, snap to target
        this.track.scrollLeft = target;
        this.isAnimating = false;
        this.springAnimationId = null;
        return;
      }

      this.springAnimationId = requestAnimationFrame(animate);
    };

    this.isAnimating = true;
    this.springAnimationId = requestAnimationFrame(animate);
  }

  /**
   * Animate momentum scrolling with spring physics and boundary bounce
   */
  animateMomentum(initialVelocity) {
    this.stopSpringAnimation();

    const { stiffness, damping, mass } = this.springConfig;
    const bounds = this.getScrollBounds();

    let position = this.track.scrollLeft;
    let velocity = initialVelocity;
    let bounceTarget = null;

    const animate = () => {
      // Check if we hit a boundary
      if (bounceTarget === null) {
        if (position < bounds.min) {
          bounceTarget = bounds.min;
        } else if (position > bounds.max) {
          bounceTarget = bounds.max;
        }
      }

      if (bounceTarget !== null) {
        // Apply spring force toward boundary
        const displacement = position - bounceTarget;
        const springForce = -this.bounceConfig.bounceStiffness * displacement;
        const dampingForce = -this.bounceConfig.bounceDamping * velocity;
        const acceleration = (springForce + dampingForce) / mass;

        const dt = 1 / 60;
        velocity += acceleration * dt;
        position += velocity * dt;
      } else {
        // Normal momentum with friction
        const friction = 0.95; // Friction coefficient
        velocity *= friction;
        position += velocity / 60;
      }

      // Apply position
      this.track.scrollLeft = position;

      // Check if animation should stop
      const velocityMagnitude = Math.abs(velocity);

      if (bounceTarget !== null) {
        const positionDelta = Math.abs(position - bounceTarget);
        if (positionDelta < 0.5 && velocityMagnitude < 10) {
          this.track.scrollLeft = bounceTarget;
          this.isAnimating = false;
          this.springAnimationId = null;
          return;
        }
      } else if (velocityMagnitude < 5) {
        // Momentum depleted
        this.isAnimating = false;
        this.springAnimationId = null;
        return;
      }

      this.springAnimationId = requestAnimationFrame(animate);
    };

    this.isAnimating = true;
    this.springAnimationId = requestAnimationFrame(animate);
  }

  /**
   * Stop any ongoing spring animation
   */
  stopSpringAnimation() {
    if (this.springAnimationId) {
      cancelAnimationFrame(this.springAnimationId);
      this.springAnimationId = null;
    }
    this.isAnimating = false;
  }

  /**
   * Bind wheel events for smooth vertical-to-horizontal scroll
   */
  bindWheelEvents() {
    if (!this.track) return;

    // Accumulated wheel delta for smooth animation
    this.wheelDelta = 0;
    this.wheelAnimationId = null;

    this.track.addEventListener(
      "wheel",
      (e) => {
        // Don't modify wheel behavior in selected mode
        if (this.mode === "selected") return;

        // Check if vertical scroll (deltaY) is dominant
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          e.preventDefault();

          // Accumulate wheel delta
          this.wheelDelta += e.deltaY;

          // Start smooth animation if not already running
          if (!this.wheelAnimationId) {
            this.animateWheelScroll();
          }
        }
      },
      { passive: false },
    );
  }

  /**
   * Animate wheel scroll with spring physics
   */
  animateWheelScroll() {
    const { stiffness, damping, mass } = this.springConfig;
    const bounds = this.getScrollBounds();

    let position = this.track.scrollLeft;
    let velocity = 0;

    const animate = () => {
      // Calculate target based on accumulated wheel delta
      let target = this.track.scrollLeft + this.wheelDelta;

      // Reset wheel delta after reading
      this.wheelDelta = 0;

      // Clamp target to bounds
      target = Math.max(bounds.min, Math.min(bounds.max, target));

      // Spring force calculation
      const displacement = position - target;
      const springForce = -stiffness * displacement;
      const dampingForce = -damping * velocity;
      const acceleration = (springForce + dampingForce) / mass;

      const dt = 1 / 60;
      velocity += acceleration * dt;
      position += velocity * dt;

      // Apply position
      this.track.scrollLeft = position;

      // Check if we should continue (include any new wheel input)
      const positionDelta = Math.abs(position - target);
      const velocityMagnitude = Math.abs(velocity);

      if (
        positionDelta < 0.5 &&
        velocityMagnitude < 10 &&
        this.wheelDelta === 0
      ) {
        this.track.scrollLeft = target;
        this.wheelAnimationId = null;
        return;
      }

      this.wheelAnimationId = requestAnimationFrame(animate);
    };

    this.wheelAnimationId = requestAnimationFrame(animate);
  }

  handleScroll() {
    // Clear highlight when user scrolls manually (not programmatic scroll from highlighting)
    if (this.highlightedIndex !== -1 && !this.isProgrammaticScroll) {
      this.clearHighlight();
    }

    // Update progress bar
    this.updateProgress();

    // Debounce scroll end detection
    this.isScrolling = true;
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.isScrolling = false;
    }, 150);
  }

  /**
   * Handle drawer vertical scroll: reveal header when scrolling up by 30px,
   * hide it when scrolling down by 50px. Always show when at top of list.
   */
  handleDrawerScroll() {
    if (!this.drawer || !this.drawerHeader) return;

    const state = this._drawerHeaderState;
    const scrollTop = this.drawer.scrollTop;
    const headerHeight = this.drawerHeader.offsetHeight || 0;
    const delta = scrollTop - state.lastTop;
    state.lastTop = scrollTop;

    // While any drawer filter dropdown is open, keep header visible so the
    // overlay continues to cover the drawer (and so it doesn't get clipped
    // by header transforms creating a containing block).
    if (this.drawerHeader.querySelector(".js-custom-select.is-open")) {
      state.downDelta = 0;
      state.upDelta = 0;
      if (state.hidden) {
        state.hidden = false;
        this.drawerHeader.classList.remove("is-hidden");
      }
      return;
    }

    // At/near top: reset to visible (sticky natural position)
    if (scrollTop <= headerHeight) {
      state.downDelta = 0;
      state.upDelta = 0;
      if (state.hidden) {
        state.hidden = false;
        this.drawerHeader.classList.remove("is-hidden");
      }
      return;
    }

    if (delta > 0) {
      // Scrolling down
      state.downDelta += delta;
      state.upDelta = 0;
      if (
        !state.hidden &&
        state.downDelta >= this._hideDrawerHeaderDownThreshold
      ) {
        state.hidden = true;
        this.drawerHeader.classList.add("is-hidden");
      }
    } else if (delta < 0) {
      // Scrolling up
      state.upDelta += -delta;
      state.downDelta = 0;
      if (state.hidden && state.upDelta >= this._showDrawerHeaderUpThreshold) {
        state.hidden = false;
        this.drawerHeader.classList.remove("is-hidden");
      }
    }
  }

  /**
   * Reset drawer header reveal state (e.g. when drawer is closed/reopened).
   */
  resetDrawerHeaderState() {
    if (!this.drawerHeader) return;
    this._drawerHeaderState.lastTop = 0;
    this._drawerHeaderState.downDelta = 0;
    this._drawerHeaderState.upDelta = 0;
    this._drawerHeaderState.hidden = false;
    this.drawerHeader.classList.remove("is-hidden");
  }

  updateProgress() {
    if (!this.track || !this.progress) return;

    this.updateProgressVisibility();

    // Skip updates if progress bar is being dragged
    if (this.progress.classList.contains("is-dragging")) return;

    const scrollLeft = this.track.scrollLeft;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const cardWidth = this.cards[0]?.offsetWidth || 260;
    const gap = 4;

    // Calculate which card is most visible
    const cardWithGap = cardWidth + gap;
    const currentIndex = Math.round(scrollLeft / cardWithGap) + 1;
    const total = this.cards.length;

    // Update data attributes on progress element
    this.progress.dataset.current = Math.min(currentIndex, total);
    this.progress.dataset.total = total;

    // Trigger progress update (if CardsProgress instance exists)
    const event = new CustomEvent("cards-progress:update", {
      bubbles: true,
      detail: { current: currentIndex, total },
    });
    this.progress.dispatchEvent(event);

    // Direct update of indicator position using scroll progress for smooth movement
    const indicator = this.progress.querySelector(".js-progress-indicator");
    if (indicator) {
      const indicatorWidth = (1 / total) * 100;
      const maxPosition = 100 - indicatorWidth;
      // Use scroll progress (0-1) for smooth continuous positioning
      const scrollProgress = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      const leftPosition = scrollProgress * maxPosition;
      indicator.style.width = `${indicatorWidth}%`;
      indicator.style.left = `${leftPosition}%`;
    }
  }

  /**
   * Highlight a card (from sector dropdown selection)
   * Scrolls to center and shows overlay on other cards
   */
  highlightCard(index) {
    if (index < 0 || index >= this.cards.length) return;

    // Update dimensions first to ensure accurate calculations
    this.updateDimensions();

    this.highlightedIndex = index;
    this.container.classList.add("is-highlighted");

    // Add highlight class to specific card
    this.cards.forEach((card, i) => {
      card.classList.toggle("is-highlighted", i === index);
    });

    // Mark as programmatic scroll so handleScroll won't clear the highlight
    this.isProgrammaticScroll = true;

    // Scroll card to center - use requestAnimationFrame to ensure layout is computed
    requestAnimationFrame(() => {
      this.scrollToCard(index, true);

      // Reset the flag only after scroll has stopped
      // Use scrollend event if supported, otherwise fallback to polling
      const resetProgrammaticScroll = () => {
        this.isProgrammaticScroll = false;
      };

      if ("onscrollend" in window) {
        this.track.addEventListener("scrollend", resetProgrammaticScroll, {
          once: true,
        });
        // Fallback timeout in case scrollend doesn't fire
        setTimeout(resetProgrammaticScroll, 1500);
      } else {
        // Fallback: wait for scroll to settle using debounce
        let scrollEndTimer;
        const checkScrollEnd = () => {
          clearTimeout(scrollEndTimer);
          scrollEndTimer = setTimeout(resetProgrammaticScroll, 150);
        };
        this.track.addEventListener("scroll", checkScrollEnd);
        // Clean up after max duration
        setTimeout(() => {
          this.track.removeEventListener("scroll", checkScrollEnd);
          resetProgrammaticScroll();
        }, 1500);
      }
    });

    // Emit event
    this.emitEvent("highlight", { index });
  }

  clearHighlight() {
    const hadHighlight = this.highlightedIndex !== -1;
    this.highlightedIndex = -1;
    this.isProgrammaticScroll = false;
    this.container.classList.remove("is-highlighted");
    this.cards.forEach((card) => card.classList.remove("is-highlighted"));

    // Emit event so sector dropdown can clear its selection
    if (hadHighlight) {
      this.emitEvent("highlight-cleared");
    }
  }

  /**
   * Create a focus trap keydown handler for a container element.
   * Returns a bound handler that can be added/removed via addEventListener.
   * @param {HTMLElement} container - The element to trap focus within
   * @returns {function} keydown event handler
   */
  _createFocusTrap(container) {
    return (e) => {
      if (e.key !== "Tab" || !container) return;

      const focusable = container.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
  }

  /**
   * Announce a message to screen readers via the live region
   * @param {string} message - Text to announce
   */
  announce(message) {
    const announcer = document.querySelector(".js-a11y-announcer");
    if (!announcer) return;
    // Clear then set to trigger re-announcement of identical text
    announcer.textContent = "";
    requestAnimationFrame(() => {
      announcer.textContent = message;
    });
  }

  /**
   * Select a card (enter selected mode with drawer)
   * @param {number} index - Card index to select
   * @param {boolean} updateUrl - Whether to push URL state (default: true)
   * @param {boolean} applyUrlFilters - Whether to apply filters from URL (default: false)
   */
  selectCard(index, updateUrl = true, applyUrlFilters = false) {
    if (index < 0 || index >= this.cards.length) return;

    this.activeIndex = index;
    this.mode = "selected";
    this.container.classList.add("is-selected");
    this.clearHighlight();

    // Selected mode darkens and de-emphasizes the surrounding dashboard while
    // keeping the active card + drawer interaction layer above it.
    // Show header overlay
    if (this.headerOverlay) {
      this.headerOverlay.classList.add("opacity-100");
      this.headerOverlay.classList.remove("pointer-events-none");
    }

    // Raise logo above overlay
    if (this.headerLogo) {
      this.headerLogo.classList.add("z-11");
    }

    // Lower z-index of dashboard elements behind overlay
    if (this.viewFilter) {
      this.viewFilter.classList.remove("z-11");
      this.viewFilter.classList.add("z-9");
    }
    if (this.dashboardBottom) {
      this.dashboardBottom.classList.remove("z-11");
      this.dashboardBottom.classList.add("z-9");
    }

    // Update card states
    this.updateCardStates();

    // Drawer content is lazy-loaded so selecting a card can also restore the
    // previously encoded URL filters before the request is made.
    this.updateDrawer(index, applyUrlFilters);

    // Prevent body scroll
    document.body.style.overflow = "hidden";

    // Center the active card in the available area
    requestAnimationFrame(() => {
      this.centerActiveCard();
    });

    // The browser URL mirrors the selected taxonomy card so back/forward can
    // reconstruct selected mode without inventing separate client-side routes.
    if (updateUrl) {
      const card = this.cards[index];
      const taxonomy = card?.dataset.taxonomy;
      const slug = card?.dataset.slug;

      if (taxonomy && slug) {
        // Build URL based on taxonomy
        const taxonomyPath =
          taxonomy === "sector" ? "sector" : "responsible-party";

        // Preserve filter query params when updating URL
        const url = new URL(window.location.href);
        url.pathname = `/${taxonomyPath}/${slug}/`;

        // Push state with card data for back/forward navigation
        history.pushState(
          {
            scrollingCards: true,
            cardIndex: index,
            taxonomy,
            slug,
          },
          "",
          url.toString(),
        );
      }
    }

    // Remove inert so drawer becomes interactive
    this.drawer?.removeAttribute("inert");

    // Emit event
    this.emitEvent("select", { index });

    // Move focus to drawer container (no visible ring; trap + SR announcement still work)
    if (this.drawer) {
      this.drawer.focus({ preventScroll: true });
    }

    // Activate drawer focus trap
    document.addEventListener("keydown", this._drawerFocusTrap);

    // Announce card selection
    const cardTitle =
      this.cards[index]?.querySelector(".js-card-title")?.textContent || "";
    this.announce(`${cardTitle} valitud. Paneel avatud.`);
  }

  /**
   * Exit selected mode and return to scroll mode
   * @param {boolean} updateUrl - Whether to push dashboard URL (default: true)
   */
  exitSelectedMode(updateUrl = true) {
    // Store reference to the previously active card for focus restoration
    const previousCard =
      this.activeIndex >= 0 ? this.cards[this.activeIndex] : null;

    this.activeIndex = -1;
    this.mode = "scroll";
    this.container.classList.remove("is-selected");

    // Hide header overlay
    if (this.headerOverlay) {
      this.headerOverlay.classList.remove("opacity-100");
      this.headerOverlay.classList.add("pointer-events-none");
    }

    // Reset logo z-index
    if (this.headerLogo) {
      this.headerLogo.classList.remove("z-11");
    }

    // Restore z-index of dashboard elements
    if (this.viewFilter) {
      this.viewFilter.classList.remove("z-9");
      this.viewFilter.classList.add("z-11");
    }
    if (this.dashboardBottom) {
      this.dashboardBottom.classList.remove("z-9");
      this.dashboardBottom.classList.add("z-11");
    }

    // Clear filters and URL params
    this.clearFilters();

    // Clear card states
    this.cards.forEach((card) => {
      card.classList.remove("is-active", "is-adjacent", "is-selected");
      const article = card.querySelector(".js-proposal-card");
      if (article) article.classList.remove("is-selected");
    });

    // Reset nav position
    if (this.nav) {
      this.nav.style.left = "";
      this.nav.style.translate = "";
    }

    // Restore body scroll
    document.body.style.overflow = "";

    // Reset drawer header reveal state for next open
    this.resetDrawerHeaderState();

    // Update URL to dashboard
    if (updateUrl) {
      history.pushState(null, "", this.dashboardUrl);
    }

    // Emit event
    this.emitEvent("deselect", {});

    // Restore focus to the previously active card
    if (previousCard) {
      const focusTarget = previousCard.querySelector(".js-proposal-card");
      if (focusTarget) focusTarget.focus();
    }

    // Deactivate drawer focus trap
    document.removeEventListener("keydown", this._drawerFocusTrap);

    // Restore inert on drawer so it's hidden from tab order
    this.drawer?.setAttribute("inert", "");

    // Announce deselection
    this.announce("Paneel suletud.");
  }

  updateCardStates() {
    this.cards.forEach((card, i) => {
      card.classList.remove("is-active", "is-adjacent");
      const article = card.querySelector(".js-proposal-card");

      if (i === this.activeIndex) {
        card.classList.add("is-active", "is-selected");
        if (article) article.classList.add("is-selected");
      } else {
        card.classList.remove("is-selected");
        if (article) article.classList.remove("is-selected");
        if (Math.abs(i - this.activeIndex) === 1) {
          card.classList.add("is-adjacent");
        }
      }
    });

    // Update nav button states
    if (this.prevBtn) {
      this.prevBtn.disabled = this.activeIndex <= 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.activeIndex >= this.cards.length - 1;
    }
  }

  navigate(direction) {
    const newIndex = this.activeIndex + direction;
    if (newIndex >= 0 && newIndex < this.cards.length) {
      this.activeIndex = newIndex;
      this.updateCardStates();
      this.updateDrawer(newIndex);
      this.centerActiveCard();
      this.emitEvent("navigate", { index: newIndex });

      // Announce navigation
      const title =
        this.cards[newIndex]?.querySelector(".js-card-title")?.textContent ||
        "";
      this.announce(`Kaart ${newIndex + 1}/${this.cards.length}: ${title}`);
    }
  }

  /**
   * Center the active card in the available area (viewport minus drawer)
   * Used in selected mode
   */
  centerActiveCard() {
    if (this.activeIndex < 0 || !this.track || !this.cardsWrapper) return;

    const card = this.cards[this.activeIndex];
    if (!card) return;

    // Calculate available width (viewport - drawer)
    const viewportWidth = window.innerWidth;
    const availableWidth = viewportWidth - this.drawerWidth;

    // Read the wrapper's left padding (not affected by card transitions)
    const wrapperStyle = getComputedStyle(this.cardsWrapper);
    const paddingLeft = parseFloat(wrapperStyle.paddingLeft) || 40;

    // Calculate the active card's FINAL center position mathematically.
    // In selected mode the active card gets mx-36 (36px margin each side).
    // Scale transforms (scale-115/85/90) don't affect layout positions.
    // This avoids reading transitioning getBoundingClientRect values.
    const activeMargin = 36;
    const stride = this.cardWidth + this.cardGap;
    const cardCenterInTrack =
      paddingLeft +
      this.activeIndex * stride +
      activeMargin +
      this.cardWidth / 2;

    // Where we want the card center to be (center of available space)
    const targetCenter = availableWidth / 2;

    // Calculate scroll position
    const scrollTarget = cardCenterInTrack - targetCenter;

    this.track.scrollTo({
      left: Math.max(0, scrollTarget),
      behavior: "smooth",
    });

    // Update nav position to be centered in available area
    this.updateNavPosition(availableWidth);
  }

  /**
   * Update nav position to center in available area
   */
  updateNavPosition(availableWidth) {
    if (!this.nav) return;

    // Position nav at center of available width
    const leftPosition = availableWidth / 2;
    this.nav.style.left = `${leftPosition}px`;
    this.nav.style.translate = "-50%";
  }

  /**
   * Scroll to center a card in the viewport (used for highlighting)
   */
  scrollToCard(index, center = false) {
    const card = this.cards[index];
    if (!card || !this.track || !this.cardsWrapper) return;

    // Get the cards wrapper's left padding
    const wrapperStyle = getComputedStyle(this.cardsWrapper);
    const paddingLeft = parseFloat(wrapperStyle.paddingLeft) || 40;

    // Calculate card position in the track
    const stride = this.cardWidth + this.cardGap;
    const cardLeftPosition = paddingLeft + index * stride;
    const cardCenterInTrack = cardLeftPosition + this.cardWidth / 2;

    let scrollTarget;
    if (center) {
      // Center the card in the full viewport width
      const viewportCenter = window.innerWidth / 2;
      scrollTarget = cardCenterInTrack - viewportCenter;
    } else {
      // Just make visible with padding
      scrollTarget = cardLeftPosition - 40;
    }

    this.track.scrollTo({
      left: Math.max(0, scrollTarget),
      behavior: "smooth",
    });
  }

  updateDrawer(index, applyUrlFilters = false) {
    if (!this.drawer) return;

    const card = this.cards[index];
    if (!card) return;

    // Get data from card attributes
    const termId = card.dataset.termId;
    const taxonomy = card.dataset.taxonomy;
    const slug = card.dataset.slug;

    // Get title and count from card content for immediate feedback
    const proposalCard = card.querySelector(".js-proposal-card");
    const title =
      proposalCard?.querySelector(".js-card-title")?.textContent || "";
    const count =
      proposalCard?.querySelector(".js-card-count")?.textContent || "";

    // Update drawer title immediately
    if (this.drawerTitle) {
      this.drawerTitle.textContent = title;
    }

    // Update drawer count with placeholder
    if (this.drawerCount) {
      this.drawerCount.innerHTML = `Ettepanekuid: <strong>${count}</strong>`;
    }

    // Handle filters
    if (applyUrlFilters) {
      // Apply filters from URL (on direct URL access)
      this.currentFilters = this.getFiltersFromUrl();
    } else {
      // Clear filters when switching cards
      this.clearFilters();
    }

    // Load drawer content via AJAX
    if (termId && taxonomy) {
      this.loadDrawerContent(termId, taxonomy, this.currentFilters);
    }
  }

  /**
   * Load drawer proposals via AJAX
   * @param {string} termId - Term ID
   * @param {string} taxonomy - Taxonomy slug
   * @param {Object} filters - Optional filters { statuses: [], parties: [] }
   * @param {boolean} updateFilters - Whether to update filter dropdown options (default: true)
   */
  async loadDrawerContent(
    termId,
    taxonomy,
    filters = {},
    updateFilters = true,
  ) {
    // Abort any pending request
    if (this.currentLoadController) {
      this.currentLoadController.abort();
    }
    this.currentLoadController = new AbortController();

    // Show loading state
    if (this.drawerLoading) {
      this.drawerLoading.classList.remove("hidden");
      this.drawerLoading.classList.add("flex");
    }
    if (this.drawerProposals) {
      this.drawerProposals.classList.add("hidden");
    }

    try {
      // Build REST URL with query params
      const restUrl = (window.APP_DATA?.rest_url || "/wp-json/") + "oska/v1";
      const params = new URLSearchParams();
      params.set("term_id", termId);
      params.set("taxonomy", taxonomy);

      // Add filters if provided
      if (filters.statuses?.length) {
        filters.statuses.forEach((s) => params.append("statuses[]", s));
      }
      if (filters.parties?.length) {
        filters.parties.forEach((p) => params.append("parties[]", p));
      }

      const response = await fetch(
        `${restUrl}/proposals/drawer?${params.toString()}`,
        {
          method: "GET",
          signal: this.currentLoadController.signal,
        },
      );

      const result = await response.json().catch(() => null);

      if (response.ok) {
        // Update drawer content
        if (this.drawerProposals) {
          this.drawerProposals.innerHTML = result.html;
          this.drawerProposals.classList.remove("hidden");
        }

        // Update count
        if (this.drawerCount) {
          this.drawerCount.innerHTML = `Ettepanekuid: <strong>${result.count}</strong>`;
        }

        // Update title if provided
        if (result.title && this.drawerTitle) {
          this.drawerTitle.textContent = result.title;
        }

        // Update filter options if requested (on card change, not on filter change)
        if (
          updateFilters &&
          result.available_statuses &&
          result.available_parties
        ) {
          this.updateFilterOptions(
            result.available_statuses,
            result.available_parties,
          );
        }

        // Re-initialize proposal item event listeners
        this.initDrawerProposalItems();

        // Announce loaded content
        this.announce(`${result.count} ettepanekut laetud.`);
      } else {
        console.error(
          "Failed to load drawer content:",
          result?.message || `Request failed with status ${response.status}`,
        );
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error loading drawer content:", error);
      }
    } finally {
      // Hide loading state
      if (this.drawerLoading) {
        this.drawerLoading.classList.add("hidden");
        this.drawerLoading.classList.remove("flex");
      }
      this.currentLoadController = null;
    }
  }

  /**
   * Initialize event listeners for dynamically loaded proposal items
   */
  initDrawerProposalItems() {
    if (!this.drawerProposals) return;

    const items = this.drawerProposals.querySelectorAll(".js-proposal-item");
    items.forEach((item) => {
      item.addEventListener("click", (e) => {
        e.preventDefault();
        const proposalId = item.dataset.id;
        this.openProposal(proposalId);
      });

      item.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          item.click();
        }
      });
    });
  }

  /**
   * Open a proposal detail modal
   * @param {string|number} proposalId - Proposal post ID
   * @param {boolean} updateUrl - Whether to update browser URL (default: true)
   * @param {boolean} animate - Whether to animate the opening (default: true)
   */
  openProposal(proposalId, updateUrl = true, animate = true) {
    if (!this.proposalModal) return;

    this.currentProposalId = proposalId;

    // Store the element that triggered the modal for focus restoration
    this._proposalTrigger = document.activeElement;

    // Disable animation if requested (for direct URL access)
    if (!animate) {
      this.proposalModal.classList.add("no-transition");
      this.proposalModalOverlay?.classList.add("no-transition");
    }

    // Show overlay
    if (this.proposalModalOverlay) {
      this.proposalModalOverlay.classList.remove("invisible", "opacity-0");
      this.proposalModalOverlay.setAttribute("aria-hidden", "false");
    }

    // Show modal
    this.proposalModal.classList.remove("translate-x-full");
    this.proposalModal.setAttribute("aria-hidden", "false");
    this.proposalModal.removeAttribute("inert");

    // Load proposal content
    this.loadProposalContent(proposalId, updateUrl);

    // Re-enable animation after a frame
    if (!animate) {
      requestAnimationFrame(() => {
        this.proposalModal.classList.remove("no-transition");
        this.proposalModalOverlay?.classList.remove("no-transition");
      });
    }

    // Emit event
    this.emitEvent("proposal-open", { proposalId });

    // Move focus to modal container (no visible ring; trap still works)
    this.proposalModal.focus({ preventScroll: true });

    // Activate proposal modal focus trap (replaces drawer trap)
    document.removeEventListener("keydown", this._drawerFocusTrap);
    document.addEventListener("keydown", this._proposalFocusTrap);
  }

  /**
   * Close the proposal detail modal
   * @param {boolean} updateUrl - Whether to update browser URL (default: true)
   */
  closeProposal(updateUrl = true) {
    if (!this.proposalModal) return;

    // Hide overlay
    if (this.proposalModalOverlay) {
      this.proposalModalOverlay.classList.add("invisible", "opacity-0");
      this.proposalModalOverlay.setAttribute("aria-hidden", "true");
    }

    // Hide modal
    this.proposalModal.classList.add("translate-x-full");
    this.proposalModal.setAttribute("aria-hidden", "true");
    this.proposalModal.setAttribute("inert", "");

    // Update URL - go back to the sector/taxonomy URL
    if (updateUrl && this.activeIndex >= 0) {
      const card = this.cards[this.activeIndex];
      const taxonomy = card?.dataset.taxonomy;
      const slug = card?.dataset.slug;

      if (taxonomy && slug) {
        const taxonomyPath =
          taxonomy === "sector" ? "sector" : "responsible-party";
        const newUrl = `/${taxonomyPath}/${slug}/`;
        history.pushState(
          { scrollingCards: true, cardIndex: this.activeIndex, taxonomy, slug },
          "",
          newUrl,
        );
      }
    }

    this.currentProposalId = null;
    this.currentProposalSlug = null;

    // Emit event
    this.emitEvent("proposal-close", {});

    // Restore focus to the element that triggered the modal
    if (this._proposalTrigger && this._proposalTrigger.isConnected) {
      this._proposalTrigger.focus();
    }
    this._proposalTrigger = null;

    // Deactivate proposal focus trap, restore drawer trap
    document.removeEventListener("keydown", this._proposalFocusTrap);
    if (this.mode === "selected") {
      document.addEventListener("keydown", this._drawerFocusTrap);
    }
  }

  /**
   * Load proposal content via AJAX
   * @param {string|number} proposalId - Proposal post ID
   * @param {boolean} updateUrl - Whether to update browser URL after loading
   */
  async loadProposalContent(proposalId, updateUrl = true) {
    // Abort any pending request
    if (this.proposalLoadController) {
      this.proposalLoadController.abort();
    }
    this.proposalLoadController = new AbortController();

    // Show loading state
    if (this.proposalModalLoading) {
      this.proposalModalLoading.classList.remove("hidden");
      this.proposalModalLoading.classList.add("flex");
    }
    if (this.proposalModalContent) {
      this.proposalModalContent.innerHTML = "";
    }

    try {
      // Fetch proposal detail from REST API
      const restUrl = (window.APP_DATA?.rest_url || "/wp-json/") + "oska/v1";
      const response = await fetch(
        `${restUrl}/proposals/${encodeURIComponent(proposalId)}`,
        {
          method: "GET",
          signal: this.proposalLoadController.signal,
        },
      );

      const result = await response.json();

      if (response.ok) {
        // Update modal content
        if (this.proposalModalContent) {
          this.proposalModalContent.innerHTML = result.html;

          // Initialize any charts in the loaded content
          if (typeof window.oskaInitCharts === "function") {
            window.oskaInitCharts(this.proposalModalContent);
          }
        }

        // Store slug for URL
        this.currentProposalSlug = result.slug;

        // Update URL with proposal URL
        if (updateUrl && result.url) {
          // Extract path from full URL
          const url = new URL(result.url);
          history.pushState(
            {
              scrollingCards: true,
              proposal: true,
              proposalId: proposalId,
              slug: result.slug,
              cardIndex: this.activeIndex,
            },
            "",
            url.pathname,
          );
        }
      } else {
        console.error("Failed to load proposal:", result?.message);
        if (this.proposalModalContent) {
          this.proposalModalContent.innerHTML = `
            <div class="flex flex-col items-center justify-center py-48 text-center">
              <p class="font-nunito text-16 text-text-dark">Ettepanekut ei leitud</p>
            </div>
          `;
        }
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error loading proposal:", error);
      }
    } finally {
      // Hide loading state
      if (this.proposalModalLoading) {
        this.proposalModalLoading.classList.add("hidden");
        this.proposalModalLoading.classList.remove("flex");
      }
      this.proposalLoadController = null;
    }
  }

  /**
   * Re-query the visible card set and reset scroll state.
   * Call after toggling `hidden` on `.scrolling-cards__cards` containers.
   */
  refresh() {
    this.cardsWrapper = this.container.querySelector(
      ".js-scrolling-cards-set:not([hidden])",
    );
    if (!this.cardsWrapper) return;

    this.cards = this.cardsWrapper.querySelectorAll(".js-scrolling-card");

    if (this.mode === "selected") {
      this.deselectCard();
    }
    this.clearHighlight();

    if (this.track) {
      this.track.scrollLeft = 0;
    }

    this.updateDimensions();
    this.updateProgress();
  }

  emitEvent(type, detail) {
    const event = new CustomEvent(`scrolling-cards:${type}`, {
      bubbles: true,
      detail: { ...detail, element: this.container },
    });
    this.container.dispatchEvent(event);
  }
}

/**
 * Card set keys matching the data-card-set attributes in the template.
 * Index corresponds to the view-filter button order.
 */
const CARD_SET_KEYS = ["sector", "responsible_party"];

/**
 * Initialize all scrolling cards containers
 * @returns {ScrollingCards[]} Array of initialized instances
 */
export default function initScrollingCards() {
  const containers = document.querySelectorAll("[data-scrolling-cards]");
  const instances = [];

  containers.forEach((container) => {
    const instance = new ScrollingCards(container);
    instances.push(instance);

    // Listen for view-filter toggle to swap card sets
    document.addEventListener("view-filter:change", (e) => {
      const setKey = CARD_SET_KEYS[e.detail.index];
      if (!setKey) return;

      const allSets = container.querySelectorAll("[data-card-set]");
      allSets.forEach((set) => {
        if (set.dataset.cardSet === setKey) {
          set.removeAttribute("hidden");
          // Apply staggered entrance animation
          const cards = set.querySelectorAll(".js-scrolling-card");
          cards.forEach((card, idx) => {
            card.style.setProperty("--card-index", idx);
            card.classList.remove("is-entering");
            // Force reflow to restart animation
            void card.offsetWidth;
            card.classList.add("is-entering");
            // Remove class after animation ends to prevent re-triggering
            card.addEventListener(
              "animationend",
              () => card.classList.remove("is-entering"),
              { once: true },
            );
          });
        } else {
          set.setAttribute("hidden", "");
        }
      });

      instance.refresh();

      // Re-init donut charts for newly visible cards (skip already-initialized)
      // Then replay animation on existing charts
      container.querySelectorAll("[data-chart]").forEach((chartEl) => {
        if (chartEl.closest("[hidden]")) return;

        // If already initialized, replay the animation
        if (chartEl._donutChart) {
          chartEl._donutChart.replay();
          return;
        }
      });

      // Trigger a global re-init of donut charts
      document.dispatchEvent(new CustomEvent("donut-charts:reinit"));
    });
  });

  // Demo: Simulate dropdown highlight selection
  const highlightBtns = document.querySelectorAll("[data-highlight-card]");
  highlightBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      const index = parseInt(btn.dataset.highlightCard, 10);
      document.dispatchEvent(
        new CustomEvent("scrolling-cards:highlight", {
          detail: { cardIndex: index },
        }),
      );
    });
  });

  return instances;
}

export { ScrollingCards };
